/* eslint-disable react-refresh/only-export-components -- PageConstructor block registry exports resolver helpers, not refreshable components. */

import { Container } from "@qws-org/ui-kit";
import type { ReactNode } from "react";
import { lazy } from "react";

import type {
  Leader_Board,
  PagePreview,
  Pages,
  Schedule as ScheduleData,
  TransformedLeaderboard,
  TransformerSchedule,
} from "~/core";
import type { PageConstructorData } from "~/core/services/page-constructor.service";
import { isAccordionBlock } from "~/features/accordion/isAccordionBlock";
import { isCallToActionBlock } from "~/features/call-to-action/isCallToActionBlock";
import { isCardGallery } from "~/features/card-gallery/isCardGallery";
import { isEventCard } from "~/features/event-card/isEventCard";
import { isFeatures } from "~/features/features/isFeatures";
import { isGallerySlider } from "~/features/gallery-slider/isGallerySlider";
import { isGridBlock } from "~/features/grid-block/isGridBlock";
import { isGridBlockV2 } from "~/features/grid-block-v2/isGridV2Block";
import { isLeaderBoard } from "~/features/leader-board/isLeaderBoard";
import { isMainBannerV4 } from "~/features/main-banner-v4/isMainBannerV4";
import { MainBannerV4 } from "~/features/main-banner-v4/MainBannerV4";
import { isMainBanner } from "~/features/main-bannerV2/isMainBanner";
import { MainBannerV2 } from "~/features/main-bannerV2/MainBannerV2";
import { MainBannerV3 } from "~/features/main-bannerV3";
import { isMainBannerV3 } from "~/features/main-bannerV3/isMainBannerV3";
import { isMainSlider } from "~/features/main-slider/isMainSlider";
import { MainSlider } from "~/features/main-slider/MainSlider";
import { isPageBreadcrumbs } from "~/features/page-breadcrumbs/isPageBreadcrumbs";
import { PageBreadcrumbs } from "~/features/page-breadcrumbs/PageBreadcrumbs";
import { isPageLinks } from "~/features/page-links/isPageLinks";
import { isPaymentMethods } from "~/features/payment-methods/isPaymentMethods";
import { isReviewSlider } from "~/features/review-slider/isReviewSlider";
import { isRowSlider } from "~/features/row-slider/isRowSlider";
import { isRowTabs } from "~/features/row-tabs/isRowTabs";
import { isSchedule } from "~/features/schedule/isSchedule";
import { isSocialNetworks } from "~/features/social-network/isSocialNetworks";
import { isSpacer } from "~/features/spacer/isSpacer";
import { isSteps } from "~/features/steps/isSteps";
import { isTextAndImage } from "~/features/text-and-image/isTextAndImage";
import { isTextBlock } from "~/features/text-block/isTextBlock";
import { isTextHighlighter } from "~/features/text-highlighter/isTextHighlighter";
import { isTextWithAnchors } from "~/features/text-with-anchors/isTextWithAnchors";
import { isWelcomeOfferBanner } from "~/features/welcome-offer-banner/isWelcomeOfferBanner";
import type { useDirectus } from "~/shared/utils/directus/useDirectus";
import type { useAction } from "~/widgets/use-action";

import type { BlockType } from "./min-heights";

const LazyAccordion = lazy(() =>
  import("~/features/accordion/Accordion").then((m) => ({
    default: m.Accordion,
  })),
);
const LazyCallToActionBlock = lazy(() =>
  import("~/features/call-to-action/CallToActionBlock").then((m) => ({
    default: m.CallToActionBlock,
  })),
);
const LazyCardGallery = lazy(() =>
  import("~/features/card-gallery/CardGallery").then((m) => ({
    default: m.CardGallery,
  })),
);
const LazyEventCard = lazy(() =>
  import("~/features/event-card/EventCard").then((m) => ({
    default: m.EventCard,
  })),
);
const LazyFeatures = lazy(() =>
  import("~/features/features/Features").then((m) => ({
    default: m.Features,
  })),
);
const LazyGallerySlider = lazy(() =>
  import("~/features/gallery-slider/GallerySlider").then((m) => ({
    default: m.GallerySlider,
  })),
);
const LazyGridBlock = lazy(() =>
  import("~/features/grid-block/GridBlock").then((m) => ({
    default: m.GridBlock,
  })),
);
const LazyGridBlockV2 = lazy(() =>
  import("~/features/grid-block-v2/GridBlockV2").then((m) => ({
    default: m.GridBlockV2,
  })),
);
const LazyLeaderBoard = lazy(() =>
  import("~/features/leader-board/LeaderBoard").then((m) => ({
    default: m.LeaderBoard,
  })),
);
const LazyPageLinks = lazy(() =>
  import("~/features/page-links/PageLinks").then((m) => ({
    default: m.PageLinks,
  })),
);
const LazyPaymentMethods = lazy(() =>
  import("~/features/payment-methods/PaymentMethods").then((m) => ({
    default: m.PaymentMethods,
  })),
);
const LazyReviewSlider = lazy(() =>
  import("~/features/review-slider/ReviewSlider").then((m) => ({
    default: m.ReviewSlider,
  })),
);
const LazyRowSlider = lazy(() =>
  import("~/features/row-slider").then((m) => ({ default: m.RowSlider })),
);
const LazyRowTabs = lazy(() =>
  import("~/features/row-tabs").then((m) => ({ default: m.RowTabs })),
);
const LazySchedule = lazy(() =>
  import("~/features/schedule/Schedule").then((m) => ({
    default: m.Schedule,
  })),
);
const LazySocialNetworks = lazy(() =>
  import("~/features/social-network/SocialNetwork").then((m) => ({
    default: m.SocialNetworks,
  })),
);
const LazySpacer = lazy(() =>
  import("~/features/spacer/Spacer").then((m) => ({ default: m.Spacer })),
);
const LazySteps = lazy(() =>
  import("~/features/steps/Steps").then((m) => ({ default: m.Steps })),
);
const LazyTextAndImage = lazy(() =>
  import("~/features/text-and-image/TextAndImage").then((m) => ({
    default: m.TextAndImage,
  })),
);
const LazyTextBlock = lazy(() =>
  import("~/features/text-block/TextBlock").then((m) => ({
    default: m.TextBlock,
  })),
);
const LazyTextHighlighter = lazy(() =>
  import("~/features/text-highlighter/TextHighlighter").then((m) => ({
    default: m.TextHighlighter,
  })),
);
const LazyTextWithAnchors = lazy(() =>
  import("~/features/text-with-anchors/TextWithAnchors").then((m) => ({
    default: m.TextWithAnchors,
  })),
);
const LazyWelcomeOfferBanner = lazy(() =>
  import("~/features/welcome-offer-banner/WelcomeOfferBanner").then((m) => ({
    default: m.WelcomeOfferBanner,
  })),
);

export type PageConstructorRenderContext = {
  data: PageConstructorData;
  pages?: Array<PagePreview>;
  getDirectusImage: ReturnType<typeof useDirectus>["getDirectusImage"];
  getUrl: (detailedPage: Pages) => string;
  isActive: (pageItem: PagePreview) => boolean;
  breadcrumbsHomeLabel?: string | null;
  onCallAction: ReturnType<typeof useAction>["callAction"];
  isActionInactive: ReturnType<typeof useAction>["isActionInactive"];
};

type DefineBlockParams<TBlock> = {
  type: BlockType;
  typenames: ReadonlyArray<string>;
  is: (item: unknown) => item is TBlock;
  render: (params: {
    item: TBlock;
    index: number;
    context: PageConstructorRenderContext;
    isPriorityBlock: boolean;
  }) => ReactNode;
  renderEager?: (params: {
    item: TBlock;
    index: number;
    context: PageConstructorRenderContext;
    isPriorityBlock: boolean;
  }) => ReactNode | null;
};

export type RenderedBlock = {
  blockType: BlockType;
  eagerBlock: ReactNode | null;
  lazyBlock: ReactNode;
};

type BlockDefinition = {
  type: BlockType;
  typenames: ReadonlyArray<string>;
  isItem: (item: unknown) => boolean;
  resolve: (params: {
    item: unknown;
    index: number;
    context: PageConstructorRenderContext;
    isPriorityBlock: boolean;
  }) => RenderedBlock | null;
};

const defineBlock = <TBlock,>({
  type,
  typenames,
  is,
  render,
  renderEager,
}: DefineBlockParams<TBlock>): BlockDefinition => ({
  type,
  typenames,
  isItem: is,
  resolve: ({ item, index, context, isPriorityBlock }) => {
    if (!is(item)) {
      return null;
    }

    return {
      blockType: type,
      eagerBlock:
        renderEager?.({ item, index, context, isPriorityBlock }) ?? null,
      lazyBlock: render({ item, index, context, isPriorityBlock }),
    };
  },
});

const fetchLeaderboardSpreadsheet = async (
  props: Leader_Board,
): Promise<TransformedLeaderboard> => {
  const response = await fetch(
    `/api/google-spreadsheet/leader-board?spreadsheetId=${props.spreadsheet_id}&range=${props.range}`,
  );

  return response.json();
};

const fetchScheduleSpreadsheet = async (
  params: ScheduleData,
): Promise<TransformerSchedule> => {
  const response = await fetch(
    `/api/google-spreadsheet/schedule?spreadsheetId=${params.spreadsheet_id}&range=${params.range}`,
  );

  return response.json();
};

const BLOCK_DEFINITIONS: ReadonlyArray<BlockDefinition> = [
  defineBlock({
    type: "rowSlider",
    typenames: ["rowSlider"],
    is: isRowSlider,
    render: ({ item, index, context }) => (
      <LazyRowSlider
        key={index}
        data={item}
        isControlInactive={context.isActionInactive}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        imageLoading="lazy"
      />
    ),
  }),
  defineBlock({
    type: "mainBannerV3",
    typenames: ["main_banner_v3"],
    is: isMainBannerV3,
    render: ({ item, index, context, isPriorityBlock }) => (
      <MainBannerV3
        key={index}
        data={item}
        isControlInactive={context.isActionInactive}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isFirstOnPage={isPriorityBlock}
      />
    ),
    renderEager: ({ item, index, context, isPriorityBlock }) => (
      <MainBannerV3
        key={index}
        data={item}
        isControlInactive={context.isActionInactive}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isFirstOnPage={isPriorityBlock}
      />
    ),
  }),
  defineBlock({
    type: "rowTabs",
    typenames: ["row_tabs"],
    is: isRowTabs,
    render: ({ item, index, context }) => (
      <LazyRowTabs
        key={index}
        data={item}
        isControlInactive={context.isActionInactive}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
      />
    ),
  }),
  defineBlock({
    type: "textBlock",
    typenames: ["text_block"],
    is: isTextBlock,
    render: ({ item, index }) => (
      <LazyTextBlock
        key={index}
        width="100%"
        maxWidth={1270}
        p={{ px: 15, py: 24 }}
        m={{ mx: "auto" }}
        md={{ p: { py: 64 } }}
        data={item}
      />
    ),
    renderEager: ({ item, index }) => (
      <LazyTextBlock
        key={index}
        width="100%"
        maxWidth={1270}
        p={{ px: 15, py: 24 }}
        m={{ mx: "auto" }}
        md={{ p: { py: 64 } }}
        data={item}
      />
    ),
  }),
  defineBlock({
    type: "mainBanner",
    typenames: ["main_banner"],
    is: isMainBanner,
    render: ({ item, index, context, isPriorityBlock }) => (
      <MainBannerV2
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        getImageUrl={context.getDirectusImage}
        isControlInactive={context.isActionInactive}
        isFirstOnPage={isPriorityBlock}
      />
    ),
    renderEager: ({ item, index, context, isPriorityBlock }) => (
      <MainBannerV2
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        getImageUrl={context.getDirectusImage}
        isControlInactive={context.isActionInactive}
        isFirstOnPage={isPriorityBlock}
      />
    ),
  }),
  defineBlock({
    type: "steps",
    typenames: ["steps"],
    is: isSteps,
    render: ({ item, index, context }) => (
      <LazySteps
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isControlInactive={context.isActionInactive}
      />
    ),
  }),
  defineBlock({
    type: "welcomeOfferBanner",
    typenames: ["welcome_offer_banner"],
    is: isWelcomeOfferBanner,
    render: ({ item, index, context, isPriorityBlock }) => (
      <LazyWelcomeOfferBanner
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        getImageUrl={context.getDirectusImage}
        isActionInactive={context.isActionInactive}
        isFirstOnPage={isPriorityBlock}
      />
    ),
    renderEager: ({ item, index, context, isPriorityBlock }) => (
      <LazyWelcomeOfferBanner
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        getImageUrl={context.getDirectusImage}
        isActionInactive={context.isActionInactive}
        isFirstOnPage={isPriorityBlock}
      />
    ),
  }),
  defineBlock({
    type: "cardGallery",
    typenames: ["card_gallery"],
    is: isCardGallery,
    render: ({ item, index, context }) => (
      <LazyCardGallery
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        getImageUrl={context.getDirectusImage}
        isControlInactive={context.isActionInactive}
        imageLoading="lazy"
        width="100%"
        maxWidth={1270}
        m={{ mx: "auto" }}
      />
    ),
    renderEager: ({ item, index, context }) => (
      <LazyCardGallery
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        getImageUrl={context.getDirectusImage}
        isControlInactive={context.isActionInactive}
        imageLoading="eager"
        width="100%"
        maxWidth={1270}
        m={{ mx: "auto" }}
      />
    ),
  }),
  defineBlock({
    type: "accordion",
    typenames: ["accordion"],
    is: isAccordionBlock,
    render: ({ item, index }) => (
      <LazyAccordion
        key={index}
        data={item}
        width="100%"
        maxWidth={1270}
        m={{ mx: "auto" }}
        d={{ p: { py: 64 } }}
        p={{ px: 15, py: 24 }}
      />
    ),
  }),
  defineBlock({
    type: "callToAction",
    typenames: ["call_to_action"],
    is: isCallToActionBlock,
    render: ({ item, index, context }) => (
      <LazyCallToActionBlock
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        getImageUrl={context.getDirectusImage}
        isActionInactive={context.isActionInactive}
      />
    ),
  }),
  defineBlock({
    type: "eventCard",
    typenames: ["event_card"],
    is: isEventCard,
    render: ({ item, index, context }) => (
      <Container
        key={index}
        maxWidth={1270}
        p={{ px: 15, py: 24 }}
        m={{ mx: "auto" }}
        md={{ p: { py: 80 } }}
      >
        <LazyEventCard
          data={item}
          getUrl={context.getUrl}
          onCallAction={context.onCallAction}
          isActionInactive={context.isActionInactive}
        />
      </Container>
    ),
  }),
  defineBlock({
    type: "features",
    typenames: ["features"],
    is: isFeatures,
    render: ({ item, index }) => (
      <LazyFeatures
        key={index}
        data={item}
        maxWidth={1270}
        width="100%"
        m={{ mx: "auto" }}
      />
    ),
  }),
  defineBlock({
    type: "gallerySlider",
    typenames: ["gallery_slider"],
    is: isGallerySlider,
    render: ({ item, index, context }) => (
      <LazyGallerySlider
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isControlInactive={context.isActionInactive}
        maxWidth={1270}
        width="100%"
        m={{ mx: "auto" }}
      />
    ),
  }),
  defineBlock({
    type: "gridBlock",
    typenames: ["grid_block"],
    is: isGridBlock,
    render: ({ item, index, context }) => (
      <LazyGridBlock
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isControlInactive={context.isActionInactive}
        imageLoading="lazy"
        m={{ mx: "auto" }}
        maxWidth={1270}
        width="100%"
      />
    ),
    renderEager: ({ item, index, context }) => (
      <LazyGridBlock
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isControlInactive={context.isActionInactive}
        imageLoading="eager"
        m={{ mx: "auto" }}
        maxWidth={1270}
        width="100%"
      />
    ),
  }),
  defineBlock({
    type: "leaderBoard",
    typenames: ["leader_board"],
    is: isLeaderBoard,
    render: ({ item, index }) => (
      <Container
        key={index}
        m={{ mx: "auto" }}
        maxWidth={1270}
        width="100%"
        p={{ px: 15 }}
      >
        <LazyLeaderBoard
          data={item}
          fetchSpreadsheet={fetchLeaderboardSpreadsheet}
        />
      </Container>
    ),
  }),
  defineBlock({
    type: "pageLinks",
    typenames: ["page_links"],
    is: isPageLinks,
    render: ({ item, index, context }) => (
      <LazyPageLinks
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isControlInactive={context.isActionInactive}
        maxWidth={1270}
        width="100%"
        m={{ mx: "auto" }}
      />
    ),
  }),
  defineBlock({
    type: "reviewSlider",
    typenames: ["review_slider"],
    is: isReviewSlider,
    render: ({ item, index, context }) => (
      <LazyReviewSlider
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isControlInactive={context.isActionInactive}
        maxWidth={1270}
        m={{ mx: "auto" }}
      />
    ),
  }),
  defineBlock({
    type: "schedule",
    typenames: ["schedule"],
    is: isSchedule,
    render: ({ item, index }) => (
      <LazySchedule
        key={index}
        data={item}
        fetchSpreadsheet={fetchScheduleSpreadsheet}
        maxWidth={1270}
        m={{ mx: "auto" }}
        width="100%"
      />
    ),
  }),
  defineBlock({
    type: "socialNetworks",
    typenames: ["social_networks"],
    is: isSocialNetworks,
    render: ({ item, index, context }) => (
      <LazySocialNetworks
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isActionInactive={context.isActionInactive}
        maxWidth={1270}
        m={{ mx: "auto" }}
      />
    ),
  }),
  defineBlock({
    type: "textAndImage",
    typenames: ["text_and_image"],
    is: isTextAndImage,
    render: ({ item, index, context }) => (
      <LazyTextAndImage
        key={index}
        maxWidth={1270}
        p={{ px: 15, py: 24 }}
        md={{ p: { py: 64 } }}
        m={{ mx: "auto" }}
        width="100%"
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isControlInactive={context.isActionInactive}
      />
    ),
  }),
  defineBlock({
    type: "textHighlighter",
    typenames: ["text_highlighter"],
    is: isTextHighlighter,
    render: ({ item, index, context }) => (
      <LazyTextHighlighter
        key={index}
        data={item}
        getImageUrl={context.getDirectusImage}
      />
    ),
  }),
  defineBlock({
    type: "pageBreadcrumbs",
    typenames: ["breadcrumbs"],
    is: isPageBreadcrumbs,
    render: ({ index, context }) => (
      <PageBreadcrumbs
        key={index}
        data={context.data}
        homeLabel={context.breadcrumbsHomeLabel}
      />
    ),
    renderEager: ({ index, context }) => (
      <PageBreadcrumbs
        key={index}
        data={context.data}
        homeLabel={context.breadcrumbsHomeLabel}
      />
    ),
  }),
  defineBlock({
    type: "spacer",
    typenames: ["spacer"],
    is: isSpacer,
    render: ({ item, index }) => <LazySpacer key={index} data={item} />,
  }),
  defineBlock({
    type: "textWithAnchors",
    typenames: ["text_with_anchors"],
    is: isTextWithAnchors,
    render: ({ item, index, context }) => (
      <LazyTextWithAnchors
        key={index}
        width="100%"
        maxWidth={1270}
        p={{ px: 15, py: 24 }}
        m={{ mx: "auto" }}
        md={{ p: { py: 64 } }}
        getPageUrl={context.getUrl}
        isActive={context.isActive}
        data={item}
        pages={context.pages}
      />
    ),
  }),
  defineBlock({
    type: "mainSlider",
    typenames: ["main_slider"],
    is: isMainSlider,
    render: ({ item, index, context, isPriorityBlock }) => (
      <MainSlider
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        getImageUrl={context.getDirectusImage}
        isControlInactive={context.isActionInactive}
        isFirstOnPage={isPriorityBlock}
      />
    ),
    renderEager: ({ item, index, context, isPriorityBlock }) => (
      <MainSlider
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        getImageUrl={context.getDirectusImage}
        isControlInactive={context.isActionInactive}
        isFirstOnPage={isPriorityBlock}
      />
    ),
  }),
  defineBlock({
    type: "gridBlockV2",
    typenames: ["grid_block_v2"],
    is: isGridBlockV2,
    render: ({ item, index, context }) => (
      <LazyGridBlockV2
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isControlInactive={context.isActionInactive}
        imageLoading="lazy"
        m={{ mx: "auto" }}
        maxWidth={1270}
        width="100%"
      />
    ),
    renderEager: ({ item, index, context }) => (
      <LazyGridBlockV2
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isControlInactive={context.isActionInactive}
        imageLoading="eager"
        m={{ mx: "auto" }}
        maxWidth={1270}
        width="100%"
      />
    ),
  }),
  defineBlock({
    type: "paymentMethods",
    typenames: ["payment_methods"],
    is: isPaymentMethods,
    render: ({ item, index, context }) => (
      <LazyPaymentMethods
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isControlInactive={context.isActionInactive}
        m={{ mx: "auto" }}
        maxWidth={1270}
        width="100%"
        p={{ px: 15, py: 24 }}
      />
    ),
  }),
  defineBlock({
    type: "mainBannerV4",
    typenames: ["main_banner_v4"],
    is: isMainBannerV4,
    render: ({ item, index, context, isPriorityBlock }) => (
      <MainBannerV4
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isControlInactive={context.isActionInactive}
        getImageUrl={context.getDirectusImage}
        isFirstOnPage={isPriorityBlock}
      />
    ),
    renderEager: ({ item, index, context, isPriorityBlock }) => (
      <MainBannerV4
        key={index}
        data={item}
        getUrl={context.getUrl}
        onCallAction={context.onCallAction}
        isControlInactive={context.isActionInactive}
        getImageUrl={context.getDirectusImage}
        isFirstOnPage={isPriorityBlock}
      />
    ),
  }),
];

const BLOCK_DEFINITIONS_BY_TYPENAME = BLOCK_DEFINITIONS.reduce<
  Record<string, ReadonlyArray<BlockDefinition>>
>((acc, definition) => {
  for (const typename of definition.typenames) {
    acc[typename] = [...(acc[typename] ?? []), definition];
  }

  return acc;
}, {});

const getItemTypename = (item: unknown): string | null => {
  if (typeof item !== "object" || item === null || !("__typename" in item)) {
    return null;
  }

  return typeof item.__typename === "string" ? item.__typename : null;
};

export const resolveBlock = (
  item: unknown,
  index: number,
  context: PageConstructorRenderContext,
  options: { isPriorityBlock?: boolean } = {},
): RenderedBlock | null => {
  const typename = getItemTypename(item);
  const definitions = typename ? BLOCK_DEFINITIONS_BY_TYPENAME[typename] : null;

  if (!definitions) {
    return null;
  }

  for (const definition of definitions) {
    const block = definition.resolve({
      item,
      index,
      context,
      isPriorityBlock: options.isPriorityBlock === true,
    });

    if (block) {
      return block;
    }
  }

  return null;
};

export const resolveBlockType = (item: unknown): BlockType | null => {
  const typename = getItemTypename(item);
  const definitions = typename ? BLOCK_DEFINITIONS_BY_TYPENAME[typename] : null;

  if (!definitions) {
    return null;
  }

  for (const definition of definitions) {
    if (definition.isItem(item)) {
      return definition.type;
    }
  }

  return null;
};
